import { Box } from '@hover/blueprint';
import { useMachine } from '@xstate/react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ExtendedMacheteViewer } from 'src/modules/designStudio/helpers/machete';
import { useSplit } from 'src/shared/hooks';

import { FeatureCategories } from '../../../constants';
import {
  ChangeViewEvent,
  UpdateStylesEvent,
  createMachine,
} from '../../../machines/building';
import { historyContext } from '../../../providers';

import { RoofConfig } from './RoofConfig';
import { TrimConfig } from './TrimConfig';
import { WallConfig } from './WallConfig';
import { WindowConfig } from './WindowConfig';

interface BuildingProps {
  jobId: number;
  canvasHeight: string;
  setLoadingBuilding: (loadingBuilding: boolean) => void;
  setViewer?: (viewer: ExtendedMacheteViewer) => void;
  metadataUrl?: string;
  geometryUrl?: string;
  menu: string | null;
}

const Building = React.memo(
  ({
    jobId,
    setLoadingBuilding,
    canvasHeight,
    setViewer,
    geometryUrl,
    menu,
    metadataUrl,
  }: BuildingProps) => {
    const canvasRef = useRef<HTMLDivElement | null>(null);
    const {
      historyState: { designConfig },
    } = useContext(historyContext);
    const machine = createMachine();
    const [state, send] = useMachine(
      machine.withContext({
        ...machine.context,
        jobId,
        canvasRef,
        geometryUrl,
        metadataUrl,
      }),
    );

    const { building, viewer } = state.context;

    useEffect(() => {
      if (viewer && setViewer) {
        setViewer(viewer);
      }
    }, [setViewer, viewer]);

    const updateStyles = useCallback(
      ({ category, update }: Pick<UpdateStylesEvent, 'category' | 'update'>) =>
        send({
          type: 'UPDATE_STYLES',
          category,
          update,
        }),
      [send],
    );

    const [windowTreatment, setWindowTreatment] = useState<
      string | undefined
    >();
    const [autoFocusTreatment, setAutoFocusTreatment] = useState<
      string | undefined
    >();
    const { client: clientPromise } = useSplit();

    useEffect(() => {
      void (async () => {
        const client = await clientPromise;

        setWindowTreatment(client?.getTreatment('DS_windows'));
        setAutoFocusTreatment(
          client?.getTreatment('DS_auto-focus-camera-on-active-menu'),
        );
      })();
    }, [clientPromise]);

    useEffect(() => {
      // TODO - figure out why we need to cast to specific event type
      send({
        type: 'CHANGE_VIEW',
        menu,
        canvasHeight,
        windowTreatment,
        autoFocusTreatment,
      } as ChangeViewEvent);
    }, [send, canvasHeight, menu, windowTreatment, autoFocusTreatment]);

    useEffect(() => {
      if (building) {
        setLoadingBuilding(false);
      }
    }, [setLoadingBuilding, building]);

    return (
      <Box>
        <Box
          height={canvasHeight}
          overflow="none"
          position="absolute"
          top={0}
          width="100vw"
        >
          <Box height="100%" ref={canvasRef} width="100%" />
        </Box>

        <WallConfig
          building={building}
          config={designConfig[FeatureCategories.WALL]}
          updateStyles={updateStyles}
        />
        <RoofConfig
          building={building}
          config={designConfig[FeatureCategories.ROOF]}
          updateStyles={updateStyles}
        />
        <TrimConfig
          building={building}
          config={designConfig[FeatureCategories.TRIM]}
          updateStyles={updateStyles}
        />
        <WindowConfig
          building={building}
          config={designConfig[FeatureCategories.WINDOW]}
          updateStyles={updateStyles}
        />
      </Box>
    );
  },
);

export { Building };
